import { useImperativeHandle, forwardRef, useCallback, useRef, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { DocListViewSettingsInput, FormVersionWithPath, ColumnStatistic } from '#types';
import type { TableColumnType, TableValue } from '#types/System.types';
import type { ColumnSpec, DocListConfig } from '../../docList/DocList.types';
import { DocListTable, type DocListTableHandle } from '../../docList/DocListTable';
import { ACTIONS_COLUMN_ID } from '../../docList/DocListTable/constants';
import { getFrozenLeftOffsets } from '../../docList/getFrozenLeftOffsets';
import {
  resetToDefaults,
  resetToDefaultsWithFilters,
  applyViewSettings,
  getViewSettingsSnapshot,
  getExportSnapshot,
  type DocListTableStore,
  type DocListTableDefaults,
} from '../../docList/createDocListTableStore';
import {
  useHomeFormVersionsBaseFilters,
  useHomeFormVersionsLabelResolver,
} from './use';

export interface HomeFormVersionsTableHandle {
  resetToDefaultView: () => void;
  applyBaseView: (baseViewId: string) => void;
  clearAllFilters: () => void;
  getViewSettingsSnapshot: () => DocListViewSettingsInput;
  applyTableViewSettings: (settings: DocListViewSettingsInput) => void;
  openAddColumnPicker: () => void;
  getStore: () => DocListTableStore;
  getExportSnapshot: () => { tableValue: TableValue; columns: TableColumnType[] } | null;
}

interface HomeFormVersionsTableProps {
  baseView: string;
  isBaseViewActive: boolean;
  onBaseViewBecameDirty?: () => void;
  activeNamedViewSettings?: DocListViewSettingsInput | null;
  onNamedViewBecameDirty?: () => void;
  configWithDynamicColumns: DocListConfig<FormVersionWithPath>;
  data: FormVersionWithPath[];
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  store: DocListTableStore;
  isSearchActive?: boolean;
}

const HomeFormVersionsTable = forwardRef<HomeFormVersionsTableHandle, HomeFormVersionsTableProps>(
  (
    {
      baseView,
      isBaseViewActive,
      onBaseViewBecameDirty,
      activeNamedViewSettings,
      onNamedViewBecameDirty,
      configWithDynamicColumns,
      data,
      isLoadingMore,
      hasMore,
      loadMore,
      store,
      isSearchActive = false,
    },
    ref
  ) => {
    const { initialFilters, resolveReportInitialFilters } = useHomeFormVersionsBaseFilters(baseView);
    const getLabel = useHomeFormVersionsLabelResolver();
    const snap = useSnapshot(store);

    const baseViewDirtyTriggeredRef = useRef<boolean>(false);
    const namedViewDirtyTriggeredRef = useRef<boolean>(false);
    const tableRef = useRef<DocListTableHandle>(null);

    const defaults = useMemo<DocListTableDefaults>(() => {
      const visibility: Record<string, boolean> = {};
      for (const col of configWithDynamicColumns.columns) {
        if (
          col.hiddenByDefault === true ||
          col.id.startsWith('field_') ||
          col.id.startsWith('entity_') ||
          col.id.startsWith('group_')
        ) {
          visibility[col.id] = false;
        }
      }
      return {
        sorting: configWithDynamicColumns.defaultSorting ?? [],
        filters: initialFilters ?? configWithDynamicColumns.defaultFilters ?? [],
        visibility,
      };
    }, [configWithDynamicColumns, initialFilters]);

    const visibleColumns = useMemo(() => {
      const effectiveVisibility: Record<string, boolean> = {
        ...defaults.visibility,
        ...snap.columnVisibility,
      };

      const allColumns = configWithDynamicColumns.columns;
      const enableActionsColumn = configWithDynamicColumns.enableActionsColumn !== false;

      const ordered: ColumnSpec<FormVersionWithPath>[] = [];

      if (snap.columnOrder.length > 0) {
        const columnMap = new Map(allColumns.map((col) => [col.id, col]));
        for (const id of snap.columnOrder) {
          if (id === ACTIONS_COLUMN_ID) continue;
          const col = columnMap.get(id);
          if (col && effectiveVisibility[id] !== false) {
            ordered.push(col);
          }
        }
      } else {
        for (const col of allColumns) {
          if (effectiveVisibility[col.id] !== false) {
            ordered.push(col);
          }
        }
      }

      if (enableActionsColumn) {
        ordered.push({
          id: ACTIONS_COLUMN_ID,
          header: '',
          accessor: () => null,
          size: 44,
          minSize: 44,
          maxSize: 44,
        });
      }

      return ordered;
    }, [configWithDynamicColumns, defaults.visibility, snap.columnVisibility, snap.columnOrder]);

    const gridTemplateColumns = useMemo(() =>
      visibleColumns
        .map((col, index) => {
          const width = snap.columnSizing[col.id] ?? col.size ?? 150;
          if (index === visibleColumns.length - 1) {
            return `minmax(${width}px, 1fr)`;
          }
          return `${width}px`;
        })
        .join(' '),
      [visibleColumns, snap.columnSizing]
    );

    const frozenLeftOffsets = useMemo(
      () => getFrozenLeftOffsets(visibleColumns.map((col) => col.id)),
      [visibleColumns]
    );

    const handleUserViewStateChange = useCallback(() => {
      if (activeNamedViewSettings) {
        if (namedViewDirtyTriggeredRef.current) return;
        namedViewDirtyTriggeredRef.current = true;
        onNamedViewBecameDirty?.();
        return;
      }

      if (!isBaseViewActive) return;
      if (baseViewDirtyTriggeredRef.current) return;
      baseViewDirtyTriggeredRef.current = true;
      onBaseViewBecameDirty?.();
    }, [activeNamedViewSettings, isBaseViewActive, onBaseViewBecameDirty, onNamedViewBecameDirty]);

    const columnStatistics = snap.columnStatistics as Record<string, ColumnStatistic>;

    const setColumnStatistic = useCallback((columnId: string, stat: ColumnStatistic | null) => {
      if (stat === null) {
        const { [columnId]: _, ...rest } = store.columnStatistics;
        store.columnStatistics = rest;
      } else {
        store.columnStatistics[columnId] = stat;
      }
      handleUserViewStateChange();
    }, [store, handleUserViewStateChange]);

    const handleRemoveColumn = useCallback((columnId: string) => {
      const currentVisible = visibleColumns
        .map(c => c.id)
        .filter(id => id !== ACTIONS_COLUMN_ID && id !== columnId);
      const enableActionsColumn = configWithDynamicColumns.enableActionsColumn !== false;

      store.columnOrder = enableActionsColumn ? [...currentVisible, ACTIONS_COLUMN_ID] : currentVisible;

      const allColumnIds = configWithDynamicColumns.columns.map(c => c.id);
      const visibility: Record<string, boolean> = {};
      for (const id of allColumnIds) {
        visibility[id] = id === ACTIONS_COLUMN_ID || (id !== columnId && currentVisible.includes(id));
      }
      store.columnVisibility = visibility;
      handleUserViewStateChange();
    }, [visibleColumns, configWithDynamicColumns, store, handleUserViewStateChange]);

    useImperativeHandle(ref, () => ({
      resetToDefaultView: () => {
        baseViewDirtyTriggeredRef.current = false;
        namedViewDirtyTriggeredRef.current = false;
        resetToDefaults(store, defaults);
      },
      applyBaseView: (baseViewId: string) => {
        baseViewDirtyTriggeredRef.current = false;
        namedViewDirtyTriggeredRef.current = false;
        const nextFilters = resolveReportInitialFilters(baseViewId);
        resetToDefaultsWithFilters(store, defaults, nextFilters);
      },
      clearAllFilters: () => {
        baseViewDirtyTriggeredRef.current = false;
        namedViewDirtyTriggeredRef.current = false;
        store.columnFilters = [];
      },
      getViewSettingsSnapshot: () => {
        const visibleIds = visibleColumns.map(c => c.id);
        return getViewSettingsSnapshot(store, visibleIds);
      },
      applyTableViewSettings: (settings: DocListViewSettingsInput) => {
        baseViewDirtyTriggeredRef.current = false;
        namedViewDirtyTriggeredRef.current = false;
        applyViewSettings(store, settings, defaults, configWithDynamicColumns);
      },
      openAddColumnPicker: () => {
        tableRef.current?.openAddColumnPicker();
      },
      getStore: () => store,
      getExportSnapshot: () =>
        getExportSnapshot(data, visibleColumns, configWithDynamicColumns.configId, getLabel),
    }), [
      store,
      defaults,
      configWithDynamicColumns,
      resolveReportInitialFilters,
      visibleColumns,
      data,
      getLabel,
    ]);

    return (
      <DocListTable
        ref={tableRef}
        store={store}
        data={data}
        config={configWithDynamicColumns}
        visibleColumns={visibleColumns}
        gridTemplateColumns={gridTemplateColumns}
        frozenLeftOffsets={frozenLeftOffsets}
        rowHeight={configWithDynamicColumns.rowHeight ?? 30}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        loadMore={loadMore}
        getLabel={getLabel}
        columnStatistics={columnStatistics}
        setColumnStatistic={setColumnStatistic}
        handleRemoveColumn={handleRemoveColumn}
        requiredColumnIds={configWithDynamicColumns.requiredColumnIds}
        enableFacetedValues={isSearchActive}
      />
    );
  }
);

HomeFormVersionsTable.displayName = 'HomeFormVersionsTable';

export default HomeFormVersionsTable;
